import BookingDetails from '@components/BookingDetails/BookingDetails';
import {
  InnerContainer,
  RightContainer,
  StepContainer,
} from '@components/Steps/Steps.styles';
import {
  InformationIcon,
  LuxTypography,
  Stack,
  useTheme,
} from '@gemini/fe-ds-core';
import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ThankYouMessageWrapper } from '@components/ThankYouPage/ThankYouPage.styles';
import useGetAppointmentData from '@hooks/useGetAppointmentData';
import { usePatientStore } from '@store/patientStore';
import { FormIds } from '@utils/enums/FormIds.enum';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { RescheduleCard, RescheduleWrapper } from './RescheduleFlow.styles';
import { CardTitleWrapper } from '@components/BookingDetails/BookingDetails.styles';
import { Helmet } from 'react-helmet';
import { IntegrationModes } from '@utils/enums/IntegrationModes.enum';
import { BrandNameList } from '@utils/consts/brandNameList';

export enum RescheduleSteps {
  RESCHEDULE,
  RESCHEDULE_SCHEDULE_EXAM,
  RESCHEDULE_CONFIRMED,
}

const ReschedulePage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const brandName = useOnlineBookingStore(state => state.brandName);
  const integrationMode = useOnlineBookingStore(state => state.integrationMode);

  const appointmentIdFromUrl = useMemo(() => {
    const queryString =
      integrationMode === IntegrationModes.WCS
        ? window.location.hash.split('?')[1] || ''
        : window.location.search.substring(1);

    return new URLSearchParams(queryString).get('appointmentId');
  }, []);

  const appointmentId =
    useOnlineBookingStore(state => state.appointmentId) || appointmentIdFromUrl;
  const { data } = useGetAppointmentData({ appointmentId });
  const updatePatient = usePatientStore(state => state.updatePatient);
  const setCurrentPatient = useOnlineBookingStore(
    state => state.setCurrentPatient,
  );

  useEffect(() => {
    setCurrentPatient(0);
  }, [setCurrentPatient]);

  useEffect(() => {
    if (data) {
      console.log('##data', data);
      updatePatient(0, FormIds.AppointmentType, {
        appointmentId: appointmentId,
        appointmentType: {
          name:
            brandName && data?.idTranslation
              ? t(
                  `appointmentTypePage.${brandName}.${data.idTranslation}.title`,
                )
              : data.appointmentType,
          idTranslation: data?.idTranslation,
          appointmentType: data.appointmentTypeId,
          isModified: false,
        },
        insurance: {
          type: data.insurance.type || undefined,
          id: data.insurance.id || '',
          name: data.insurance.name || undefined,
        },
        scheduleExam: {
          day: data.AppointmentExam.date,
          hour: data.AppointmentExam.startTime,
          doctor: data.AppointmentExam.doctorName,
        },
        //save the date also in previousSlotToDisable
        previousSlotToDisable: {
          day: data.AppointmentExam.date,
          hour: data.AppointmentExam.startTime,
          doctor: data.AppointmentExam.doctorName,
        },
        patientId: data.AppointmentExam.patientId, // settato a questo livello solo per il reschedule
      });
    }
  }, [data, updatePatient]);

  return (
    <>
      <Helmet>
        <title>{t('wcag.ob_reschedule_title')}</title>
      </Helmet>
      <RescheduleWrapper>
        <ThankYouMessageWrapper>
          <Stack
            style={{
              width: '2.75rem',
              height: '2.75rem',
              borderRadius: '100%',
              border: `1px solid ${theme.palette.LUX.obTokens.bordersColorFunctionalInfo}`,
              backgroundColor: theme.palette.LUX.obTokens.bgColorFunctionalInfo,
            }}
            justifyContent="center"
            alignItems="center"
          >
            <InformationIcon color={theme.palette.LUX.content.primary} />
          </Stack>
          <LuxTypography
            variant="heading3"
            fontWeight={brandName === BrandNameList.TO ? 700 : 400}
          >
            {t('manageAppointment.manage')}
          </LuxTypography>
        </ThankYouMessageWrapper>
        <InnerContainer>
          <StepContainer
            borderColor={
              theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
            }
          >
            <CardTitleWrapper
              borderColor={
                theme.palette.LUX.obTokens?.bordersColorNeutralSecondary
              }
            >
              <LuxTypography variant="heading4">
                {t('rescheduleAppointment.title')}
              </LuxTypography>
            </CardTitleWrapper>
            <RescheduleCard>
              <LuxTypography
                variant={'heading4'}
                fontSize={'1.125rem'}
                fontWeight={400}
              >
                {t('rescheduleAppointment.reschedule_card_title')}
              </LuxTypography>
              <LuxTypography variant={'textXs'} fontSize={'0.875rem'}>
                {t('rescheduleAppointment.reschedule_card_descr')}
              </LuxTypography>
            </RescheduleCard>
          </StepContainer>
          <RightContainer manageOrRescheduleMode>
            <BookingDetails hideEditButton manageOrRescheduleMode />
          </RightContainer>
        </InnerContainer>
      </RescheduleWrapper>
    </>
  );
};

export const useRescheduleMeta = (integrationMode?: string) => {
  const appointmentIdFromUrl = useMemo(() => {
    const queryString =
      integrationMode === IntegrationModes.WCS
        ? window.location.hash.split('?')[1] || ''
        : window.location.search.substring(1);

    return new URLSearchParams(queryString).get('appointmentId');
  }, []);
  const appointmentId =
    useOnlineBookingStore(state => state.appointmentId) || appointmentIdFromUrl;

  const { data } = useGetAppointmentData({ appointmentId });
  return {
    reschedulable: data?.reschedulable,
    appointmentStatus: data?.appointmentStatus,
  };
};

export default ReschedulePage;
